import React from 'react';
import { createPortal } from 'react-dom';
import Joyride, { CallBackProps, EVENTS, Placement, TooltipRenderProps } from 'react-joyride';
import { Subtext } from '@ui-library/typography';
import styled, { css, useTheme } from 'styled-components';

/** Where a scrim click landed, in viewport coordinates. */
export interface SpotlightClick {
  clientX: number;
  clientY: number;
}

interface Props {
  onClose: () => void;
  /**
   * `next` only: a click that lands on the spotlit element while the scrim
   * still owns pointer events. The scrim lets the pointer through only once
   * it has MOVED over the element, so a pointer already resting there or a
   * touch tap hits the scrim instead and would otherwise read as a close.
   * Called in place of `onClose` for that click, with where it landed, so
   * the caller can treat it as the control's own click.
   */
  onSpotlightClick?: (click: SpotlightClick) => void;
  spotlight: {
    placement?: Placement;
    className: string;
    padding?: number;
    offset?: number;
    /** Corner radius of the cut-out — match the spotlit control (a pill wants 40). */
    borderRadius?: number;
  };
  text: string | JSX.Element;
  /**
   * `next`: the design-system card (Figma 2946:19120) — white 208px card,
   * 12px radius, optional title, grey body, a 16px close box holding a 6px
   * glyph, an 11.5×9 pointer, over the light navy scrim. Default: the legacy
   * primary-filled bubble.
   */
  variant?: 'next';
  /** Heading above the text — `next` only. */
  title?: string;
  // To focus on a specific element within the spotlight
  tooltip?: {
    id: string;
    placement: 'bottom' | 'top';
  };
  /** Test id on the `next` card; its close control gets `${testId}-close`. */
  testId?: string;
}

/** Figma 2946:18907 / 2946:19121. */
const NEXT_CARD_WIDTH = 208;
/** Figma 2944:18848: the scrim's blur. */
const NEXT_SCRIM_BLUR_PX = 8;
/** Joyride's own overlay level; it floats the card 100 above this. */
const NEXT_SCRIM_Z_INDEX = 100;
/** The scrim mask's outer box: larger than any viewport, so it never needs re-cutting on resize. */
const NEXT_SCRIM_MASK_EXTENT = 100000;
/** How long the spotlit element must rest somewhere new before the card is re-placed over it. */
const NEXT_CARD_REPLACE_DELAY_MS = 150;

/**
 * The `next` scrim (Figma 2944:18848): a light navy wash over a blurred page,
 * with the spotlit element cut out so it stays sharp. Joyride's own overlay
 * cannot do this — it lights the spotlight with a blend mode, which cannot
 * undo a backdrop blur — so the step goes without one and this takes its
 * place. The cut-out is a mask (a `clip-path` would also clip hit testing,
 * but Chrome on a 2x display paints nothing behind an element that has both a
 * clip-path and a backdrop filter), so pointer events are handed through
 * separately: once the pointer moves over the element the scrim lets them
 * pass. Without mask support there is no hole, and then no blur either: the
 * wash alone keeps the element visible.
 */
const NextScrim = styled.div<{ $holeMask?: string; $isPointerOverSpotlit: boolean }>`
  position: fixed;
  inset: 0;
  z-index: ${NEXT_SCRIM_Z_INDEX};
  cursor: pointer;
  background: ${({ theme }) => theme.token.next.coachMark.scrim};
  pointer-events: ${({ $isPointerOverSpotlit }) => ($isPointerOverSpotlit ? 'none' : 'auto')};
  ${({ $holeMask }) =>
    $holeMask &&
    css`
      -webkit-backdrop-filter: blur(${NEXT_SCRIM_BLUR_PX}px);
      backdrop-filter: blur(${NEXT_SCRIM_BLUR_PX}px);
      -webkit-mask-image: ${$holeMask};
      mask-image: ${$holeMask};
      -webkit-mask-repeat: no-repeat;
      mask-repeat: no-repeat;
      -webkit-mask-position: 0 0;
      mask-position: 0 0;
    `}
`;

const canCutScrimHole = () =>
  typeof CSS !== 'undefined' &&
  (CSS.supports?.('mask-image', 'url(x)') || CSS.supports?.('-webkit-mask-image', 'url(x)'));

/**
 * The scrim with a rounded rectangle taken out of it, as a mask image: an SVG
 * where the outer box and the hole wind the same way and even-odd filling
 * leaves the hole unpainted, so the scrim is masked out there.
 */
const scrimHoleMask = (rect: DOMRect, padding: number, borderRadius: number) => {
  const left = rect.left - padding;
  const top = rect.top - padding;
  const right = rect.right + padding;
  const bottom = rect.bottom + padding;
  const r = Math.max(0, Math.min(borderRadius, (right - left) / 2, (bottom - top) / 2));
  const outer = `M0 0H${NEXT_SCRIM_MASK_EXTENT}V${NEXT_SCRIM_MASK_EXTENT}H0Z`;
  const hole =
    `M${left + r} ${top}H${right - r}A${r} ${r} 0 0 1 ${right} ${top + r}` +
    `V${bottom - r}A${r} ${r} 0 0 1 ${right - r} ${bottom}H${left + r}` +
    `A${r} ${r} 0 0 1 ${left} ${bottom - r}V${top + r}A${r} ${r} 0 0 1 ${left + r} ${top}Z`;
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' width='${NEXT_SCRIM_MASK_EXTENT}' height='${NEXT_SCRIM_MASK_EXTENT}'>` +
    `<path fill-rule='evenodd' d='${outer}${hole}'/></svg>`;
  return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
};

const areRectsEqual = (a: DOMRect | undefined, b: DOMRect) =>
  !!a && a.left === b.left && a.top === b.top && a.width === b.width && a.height === b.height;

/**
 * The spotlit element's box, kept current: measured before paint and again
 * on resize, scroll and any size change of the element or its ancestors —
 * the element moves whenever something around it reflows (its row, the
 * sidebar settling its height, the rail expanding) — coalesced to one
 * measure per frame, and only a box that moved re-renders.
 */
const useSpotlitRect = (isEnabled: boolean, className: string) => {
  const [rect, setRect] = React.useState<DOMRect>();
  React.useLayoutEffect(() => {
    if (!isEnabled) {
      setRect(undefined);
      return undefined;
    }
    const spotlit = document.querySelector(`.${className}`);
    if (!spotlit) {
      setRect(undefined);
      return undefined;
    }
    let frame = 0;
    const measure = () => {
      frame = 0;
      const next = spotlit.getBoundingClientRect();
      setRect((previous) => (areRectsEqual(previous, next) ? previous : next));
    };
    const schedule = () => {
      if (!frame) {
        frame = window.requestAnimationFrame(measure);
      }
    };
    measure();
    window.addEventListener('resize', schedule);
    window.visualViewport?.addEventListener('resize', schedule);
    window.visualViewport?.addEventListener('scroll', schedule);
    document.addEventListener('scroll', schedule, true);
    const observer =
      typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(schedule);
    for (let node: Element | null = spotlit; node; node = node.parentElement) {
      observer?.observe(node);
    }
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', schedule);
      window.visualViewport?.removeEventListener('resize', schedule);
      window.visualViewport?.removeEventListener('scroll', schedule);
      document.removeEventListener('scroll', schedule, true);
      observer?.disconnect();
    };
  }, [isEnabled, className]);
  return rect;
};

const isInsideRect = (rect: DOMRect, { clientX, clientY }: SpotlightClick) =>
  clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
const NEXT_POINTER_SPREAD = 11.547;
const NEXT_POINTER_LENGTH = 9.196;
const NEXT_POINTER_INSET = 14;

/* Figma 2946:18907: 16px top/bottom, 8px sides; the rows carry the inner 8px.
   The sides are 7px here because the 1px border is inside the 208px box, so
   the text column is the design's 176px, 16px in from each edge. */
const NextCard = styled.div`
  position: relative;
  box-sizing: border-box;
  width: ${NEXT_CARD_WIDTH}px;
  padding: 16px 7px;
  border-radius: 12px;
  background: ${({ theme }) => theme.token.colorBgContainer};
  /* Invisible in light mode (the scrim gives the card its edge); in dark
     mode the card and the page share a colour, so the edge is drawn. */
  border: 1px solid ${({ theme }) => theme.token.next.coachMark.cardBorder};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: left;
`;

/* Both text rows are cap-trimmed in the design (text-box-trim), which is what
   makes the card 98px tall; the launcher's Beta tag uses the same trim. */
const NextTitle = styled.p`
  margin: 0;
  padding: 0 8px;
  font-size: 13px;
  font-weight: ${({ theme }) => theme.token.next.fontWeight500};
  line-height: normal;
  text-box: trim-both cap alphabetic;
  color: ${({ theme }) => theme.token.next.palette.grey800};
`;

const NextBody = styled.p`
  margin: 0;
  padding: 0 8px;
  font-size: 13px;
  line-height: 1.44;
  letter-spacing: 0.26px;
  text-box: trim-both cap alphabetic;
  /* The mock uses grey400, which is 3.3:1 on the card — under the AA floor
     for 13px text. grey500 is the nearest tone that passes in both modes. */
  color: ${({ theme }) => theme.token.next.palette.grey500};
`;

/* Figma 2946:18916 "Symbols/close-medium": a 16px box at 6px from the top and
   right edges; the glyph sits at the box's 31.25% inset (6px), grey-300, 1px. */
const NextClose = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.token.next.palette.grey300};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.token.colorPrimary};
    outline-offset: -2px;
    border-radius: 4px;
  }
`;

/** The Figma glyph, verbatim: two 1px strokes from (0.5,0.5) to (6.5,6.5). */
const NextCloseGlyph: React.FC = () => (
  <svg
    width="7"
    height="7"
    viewBox="0 0 7 7"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path d="M0.5 0.5L6.5 6.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M0.5 6.5L6.5 0.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * The `next` card as Joyride's tooltip. Joyride's own tooltip draws a 16px
 * close glyph and its own paddings; the design is pixel-specific, so the card
 * is rendered here and the library keeps the scrim, spotlight, pointer and
 * positioning.
 */
/**
 * Joyride renders its tooltip component with its own props only, so the
 * card's test id travels by context (portals keep context).
 */
const NextTooltipTestIdContext = React.createContext<string | undefined>(undefined);

const NextTooltip: React.FC<TooltipRenderProps> = ({ closeProps, step, tooltipProps }) => {
  const testId = React.useContext(NextTooltipTestIdContext);
  // Joyride labels the surface `alertdialog`; name and describe it from the
  // card's own text so assistive tech does not meet an anonymous modal.
  const id = React.useId();
  const titleId = `${id}-title`;
  const bodyId = `${id}-body`;
  return (
    <NextCard
      {...tooltipProps}
      aria-labelledby={step.title ? titleId : bodyId}
      aria-describedby={step.title ? bodyId : undefined}
      data-testid={testId}
    >
      {step.title && (
        <NextTitle id={titleId} role="heading" aria-level={2}>
          {step.title}
        </NextTitle>
      )}
      <NextBody id={bodyId}>{step.content}</NextBody>
      <NextClose type="button" {...closeProps} data-testid={testId && `${testId}-close`}>
        <NextCloseGlyph />
      </NextClose>
    </NextCard>
  );
};

export const CoachMark = ({
  onClose,
  onSpotlightClick,
  tooltip,
  spotlight,
  text,
  variant,
  title,
  testId,
}: Props) => {
  const { token } = useTheme();
  const isNext = variant === 'next';
  const {
    className: spotlightClassName,
    placement: spotlightPlacement,
    offset: spotlightOffset = 10,
    padding: spotlightPadding = 0,
    borderRadius: spotlightRadius = 8,
  } = spotlight;
  const spotlitRect = useSpotlitRect(isNext, spotlightClassName);
  const scrimHoleMaskImage = React.useMemo(
    () =>
      spotlitRect && canCutScrimHole()
        ? scrimHoleMask(spotlitRect, spotlightPadding, spotlightRadius)
        : undefined,
    [spotlitRect, spotlightPadding, spotlightRadius],
  );

  // Like Joyride's own overlay: once the pointer has moved over the spotlit
  // element the scrim stands aside, so the element gets its hover and clicks.
  // Tracked on the document, since the scrim no longer sees the pointer then.
  const [isPointerOverSpotlit, setIsPointerOverSpotlit] = React.useState(false);
  React.useEffect(() => {
    // A new box starts over: the element may have moved out from under a
    // resting pointer, and the scrim must not stay stood aside.
    setIsPointerOverSpotlit(false);
    if (!isNext || !spotlitRect) {
      return undefined;
    }
    const handleMouseMove = ({ clientX, clientY }: MouseEvent) => {
      setIsPointerOverSpotlit(isInsideRect(spotlitRect, { clientX, clientY }));
    };
    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, [isNext, spotlitRect]);

  // Joyride places the card once, on mount. When the spotlit element settles
  // somewhere else, the card is mounted again to be placed over it — once the
  // element has rested, so an animation does not remount it every frame, and
  // never from under the keyboard: while the card holds focus (its X), the
  // re-placement waits.
  const [cardMountKey, setCardMountKey] = React.useState(0);
  const placedRectRef = React.useRef<DOMRect>();
  React.useEffect(() => {
    if (!isNext || !spotlitRect) {
      return undefined;
    }
    if (!placedRectRef.current) {
      placedRectRef.current = spotlitRect;
      return undefined;
    }
    if (areRectsEqual(placedRectRef.current, spotlitRect)) {
      return undefined;
    }
    let timer = 0;
    const replaceCard = () => {
      if (document.activeElement?.closest('[role="alertdialog"]')) {
        timer = window.setTimeout(replaceCard, NEXT_CARD_REPLACE_DELAY_MS);
        return;
      }
      placedRectRef.current = spotlitRect;
      setCardMountKey((key) => key + 1);
    };
    timer = window.setTimeout(replaceCard, NEXT_CARD_REPLACE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [isNext, spotlitRect]);

  // A scrim click: over the spotlit element (a tap, or a pointer that rested
  // there) it is that element's click, anywhere else it ends the mark.
  const handleScrimClick = (event: React.MouseEvent) => {
    const { clientX, clientY } = event;
    const spotlit = onSpotlightClick && document.querySelector(`.${spotlightClassName}`);
    if (spotlit && isInsideRect(spotlit.getBoundingClientRect(), { clientX, clientY })) {
      onSpotlightClick({ clientX, clientY });
      return;
    }
    onClose();
  };

  const legacyStyles = {
    buttonClose: {
      top: 6,
      right: 8,
      padding: 0,
      // The bubble is filled with `colorPrimary`, which the dark palette
      // lightens; `colorBgContainer` inverts with the theme and so stays
      // legible on that fill in either mode.
      color: token.colorBgContainer,
      width: 10,
      height: 10,
    },
    tooltipContent: {
      padding: 0,
    },
    tooltip: {
      borderRadius: token.borderRadiusLG,
      padding: '12px 26px 12px 12px',
    },
    tooltipContainer: {
      textAlign: 'left' as const,
    },
    options: {
      width: 230,
      backgroundColor: token.colorPrimary,
      arrowColor: token.colorPrimary,
      borderRadius: token.borderRadiusLG,
    },
  };

  const nextStyles = {
    options: {
      width: NEXT_CARD_WIDTH,
      backgroundColor: token.colorBgContainer,
      arrowColor: token.colorBgContainer,
      zIndex: NEXT_SCRIM_Z_INDEX,
    },
  };

  const handleCallback = (data: CallBackProps) => {
    // The X (and the scrim, for the legacy bubble) arrive as `close`. Joyride
    // reports it twice (after the step, then at tour end); the next card acts
    // on one, the legacy bubble keeps its callers' long-standing double call.
    if (data.action !== 'close') {
      return;
    }
    if (isNext && data.type !== EVENTS.STEP_AFTER) {
      return;
    }
    onClose();
  };

  const options = {
    steps: [
      {
        disableBeacon: true,
        hideFooter: true,
        // The next card brings its own scrim (NextScrim); without Joyride's
        // overlay its spotlight-click tracking has nothing to track.
        disableOverlay: isNext,
        spotlightClicks: !isNext,
        placement: spotlightPlacement,
        target: `.${spotlightClassName}`,
        offset: spotlightOffset,
        ...(isNext ? { title } : {}),
        content: isNext ? text : <Subtext color={token.colorBgContainer}>{text}</Subtext>,
      },
    ],
    ...(!!tooltip && {
      floaterProps: {
        target: `#${tooltip.id}`,
        placement: tooltip.placement,
      },
    }),
    ...(isNext && {
      floaterProps: {
        ...(tooltip ? { target: `#${tooltip.id}`, placement: tooltip.placement } : {}),
        // Popper would centre the pointer on the target; the design pins it
        // 14px in from the card's leading edge (the `margin` below), so the
        // arrow modifier is switched off and the margin alone places it.
        options: { arrow: { enabled: false } },
        styles: {
          // Figma 2946:19121 "Polygon 3": 11.5 wide, 9 tall, 14px in from the edge.
          arrow: {
            length: NEXT_POINTER_LENGTH,
            spread: NEXT_POINTER_SPREAD,
            margin: NEXT_POINTER_INSET,
          },
          // The design's card is flat; drop the floater's default drop-shadow.
          floater: { filter: 'none' },
        },
      },
      tooltipComponent: NextTooltip,
    }),
  };

  return (
    <NextTooltipTestIdContext.Provider value={testId}>
      {isNext &&
        createPortal(
          // Pointer-only by design, like Joyride's own overlay: the keyboard
          // closes the card from its X or Escape, which Joyride handles.
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <NextScrim
            aria-hidden
            $holeMask={scrimHoleMaskImage}
            $isPointerOverSpotlit={isPointerOverSpotlit}
            onClick={handleScrimClick}
            data-testid={testId && `${testId}-scrim`}
          />,
          document.body,
        )}
      <Joyride
        key={cardMountKey}
        callback={handleCallback}
        {...options}
        styles={isNext ? nextStyles : legacyStyles}
        disableScrolling
        spotlightPadding={spotlightPadding}
      />
    </NextTooltipTestIdContext.Provider>
  );
};
